import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, FileText } from "lucide-react";
import { format } from "date-fns";

interface RelatedInsightsProps {
  currentSlug: string;
  category?: string | null;
  tags?: string[] | null;
  limit?: number;
}

const RelatedInsights = ({ currentSlug, category, tags, limit = 3 }: RelatedInsightsProps) => {
  const { data: related } = useQuery({
    queryKey: ["related-articles", currentSlug, category, tags],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("slug, title, excerpt, category, image_url, published_at, tags")
        .eq("published", true)
        .neq("slug", currentSlug)
        .order("published_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      if (!data) return [];

      const scored = data.map((a) => {
        let score = 0;
        if (category && a.category === category) score += 5;
        if (tags && a.tags) {
          const overlap = a.tags.filter((t: string) => tags.includes(t)).length;
          score += overlap * 2;
        }
        return { article: a, score };
      });
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, limit).map((s) => s.article);
    },
  });

  if (!related || related.length === 0) return null;

  return (
    <section className="mt-16 pt-10 border-t border-border">
      <div className="mb-6">
        <span className="text-primary text-xs font-semibold tracking-wider uppercase">
          Keep Reading
        </span>
        <h2 className="font-display text-2xl font-bold mt-2">Related Insights</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {related.map((a) => (
          <Link key={a.slug} to={`/insights/${a.slug}`} className="group">
            <article className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors h-full flex flex-col">
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt={a.title}
                  className="w-full h-32 object-cover"
                />
              ) : (
                <div className="h-32 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent flex items-center justify-center">
                  <FileText className="h-8 w-8 text-primary/30 group-hover:text-primary/50 transition-colors" />
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                {a.category && (
                  <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full self-start mb-2">
                    {a.category}
                  </span>
                )}
                <h3 className="font-display text-base font-semibold leading-snug group-hover:text-primary transition-colors mb-2">
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                    {a.excerpt}
                  </p>
                )}
                {a.published_at && (
                  <div className="mt-auto flex items-center text-[11px] text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    {format(new Date(a.published_at), "MMM d, yyyy")}
                  </div>
                )}
              </div>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedInsights;